import React from 'react'
import * as yup from 'yup'

export const setupValidationLocale = () => {
  yup.setLocale({
    mixed: {
      required: 'Erforderlich'
    },
    string: {
      email: 'Ungültige Adresse'
    },
    number: {
      min: 'Muss nach 1901 liegen'
    }
  })
}

export const jsonHeaders = {
  'Accept': 'application/json',
  'Content-Type': 'application/json'
}

export class ApiError extends Error {
  constructor (obj) {
    let message = ''
    const entries = Object.entries(obj)
    for (const [key, text] of entries) {
      message = message + '' + key + ': ' + text
    }
    super(message)
  }
}

export const prepareApiRequest = ({ apiUrl, apiToken, endpoint }) => {
  return {
    url: `${apiUrl}${endpoint}`,
    headers: {
      'Authorization': `Bearer ${apiToken}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    mode: 'cors'
  }
}

export const withResponseHandling = async (res) => {
  let json
  try {
    json = await res.json()
  } catch(e) {
    return Promise.reject(new Error(`Content ist kein JSON: ${e.message}`))
  }

  if (res.ok) {
    return json
  } else {
    throw new Error(`Api Error: ${JSON.stringify(json)}`)
  }
}

export const getAkDataFromSessionStorage = () => {

  const isBrowser = typeof window != "undefined"

  if (isBrowser && window.sessionStorage) {
    // Set as variable _after_ we know this is a browser,
    // otherwise fails in SSR/node.js environment
    const storage = window.sessionStorage

    const forwardedItem = storage.getItem("ak-forwarded")
    const forwarded = (forwardedItem != null && forwardedItem.length > 0)
      ? forwardedItem
      : null

    const entrypointItem = storage.getItem("ak-entrypoint")
    const entrypoint = (entrypointItem != null && entrypointItem.length > 0)
      ? entrypointItem
      : null

    return { forwarded, entrypoint }
  } else {
    console.warn("Houston - we have a problem... - ak-forwarded and ak-entrypoint can not be read")
    return {}
  }
}

export const getWording = (organisationName, businessArea, overrides) => {
  const {
    headline,
    subline,
    teaser
  } = overrides

  let wording

  switch (businessArea) {
    case 'schueleraustausch':
      wording = {
        headline: 'Schüleraustausch-Katalog',
        subline: '',
        teaser: (<>{organisationName} schickt dir deinen Katalog kostenlos nach Hause. Bitte fülle das Formular vollständig aus.</>),
      }
      break
    case 'sprachreisen':
      wording = {
        headline: 'Sprachreisen-Broschüre',
        subline: 'Jetzt unverbindlich anfordern!',
        teaser: (<>{organisationName} schickt dir die Broschüre gratis nach Hause, damit du dich entspannt über ihre Sprachreisen informieren kannst. Bitte fülle das Formular vollständig aus. Danke!</>),
      }
      break
    default:
      throw new Error(`No default wording for businesArea '${businessArea}'`)
  }

  if (headline && headline.length) {
    wording.headline = headline
  }

  if (subline && subline.length) {
    wording.subline = subline
  }

  if (teaser && teaser.length) {
    wording.teaser = <>{teaser}</>
  }

  return wording
}

// Returns texts for headline, subline, teaser
// depending on businessArea and upsell round (starting at zero!).
//
// maxRounds and round are ignored for 'sprachreisen' (currently)
export const getUpsellWording = (businessArea, round) => {
  let wording

  switch (businessArea) {
    case 'schueleraustausch':
      switch (round) {
        case 0:
          wording = {
            headline: 'Yeah thanks! - dein Katalog ist unterwegs...',
            subline: '',
            teaser: (
              <>
                <p>
                  <strong>Pro-Tipp:</strong>{' '}
                  Hol dir hier weitere Kataloge/Werbung, um umfassend informiert zu sein und die perfekte Organisation für deinen Schüleraustausch zu finden.
                </p>
                <p>
                  <strong>Bonus: Auslandsjahr Notion Planer!</strong>{' '}
                  Als Bonus erhältst du unseren exklusiven Notion Planer für dein Auslandsjahr!
                </p>
              </>
            )
          }
          break
        case 1:
          wording = {
            headline: 'Gut gemacht! Dein Katalog ist unterwegs.',
            subline: '',
            teaser: (
              <>
                <p>
                  <strong>Optional:</strong>{' '}
                  Mach's dir einfacher und hol' dir hier weitere Kataloge/Werbung zum Vergleich!
                </p>
                <p>
                  <strong>Dein Bonus</strong>{' '}
                  Der Notion Planer ist bereits freigeschaltet und wird dir am Ende der Bestellung angezeigt.
                </p>
              </>
            )
          }
          break
        case 2:
          // round 2 and higher share the same text
        default:
          wording = {
            headline: '100% Durchblick mit allen Organisationen im Vergleich',
            subline: '',
            teaser: (
              <>
                <p>
                  <strong>Optional:</strong>{' '}
                  Mach's dir einfacher und hol' dir hier weitere Kataloge/Werbung zum Vergleich!
                </p>
                <p>
                  <strong>Bonus Video!</strong>{' '}
                  Dein Bonus Video ist freigeschaltet und wird dir am Ende der Bestellung angezeigt.
                </p>
              </>
            )
          }
      }

      break
    case 'sprachreisen':
      wording = {
        headline: 'Danke! Dein Katalog ist unterwegs...',
        subline: '',
        teaser: (<>Tipp: Um dir ein Bild von den unterschiedlichen Sprachreise-Veranstaltern zu machen, kannst du jetzt weitere kostenlose Kataloge/Werbung anfordern.</>),
      }
      break
    default:
      throw new Error(`No default upsell wording for businesArea '${businessArea}'`)
  }

  return wording
}


export const getUpsellFooterWording = (businessArea, round) => {
  let wording

  switch (businessArea) {
    case 'schueleraustausch':
      switch (round) {
        case 0:
          wording = {
            text: (
              <>
                <p>
                  <strong>Unser Bonus – exklusiv für dich:</strong>{' '}
                  Als besonderes Extra erhältst du auf der Dankeseite deiner Katalogbestellung Zugriff auf
                  unseren kostenlosen Notion Planer – dein digitales Tagebuch für die Vorbereitung,
                  Durchführung und Nachbereitung deines Auslandsjahres.
                </p>
                <p>
                  Im Notion Planer enthalten ist ein exklusives Video mit den ehemaligen Austauschschülerinnen
                  Jackie und Sima, die berichten, wie sie ihren Eltern vom geplanten Auslandsjahr erzählt
                  haben, wie deren Reaktionen ausfielen und mit welchen Argumenten sie sie überzeugen konnten!
                </p>
              </>
            )
          }
          break
        case 1:
          wording = {
            text: (
              <>
                <p>
                  <strong>Unser Bonus – exklusiv für dich:</strong>{' '}
                  Als besonderes Extra erhältst du auf der Dankeseite deiner Katalogbestellung Zugriff auf
                  unseren kostenlosen Notion Planer – dein digitales Tagebuch für die Vorbereitung,
                  Durchführung und Nachbereitung deines Auslandsjahres.
                </p>
                <p>
                  Notion Planer enthalten ist ein exklusives Video mit den ehemaligen Austauschschülerinnen
                  Jackie und Sima, die berichten, wie sie ihren Eltern vom geplanten Auslandsjahr erzählt
                  haben, wie deren Reaktionen ausfielen und mit welchen Argumenten sie sie überzeugen konnten!
                </p>
              </>
            )
          }
          break
        case 2:
          // round 2 and higher share the same text
        default:
          wording = {
            text: (
              <>
                <p>
                  <strong>Unser Bonus – exklusiv für dich:</strong>{' '}
                  Als besonderes Extra erhältst du auf der Dankeseite deiner Katalogbestellung Zugriff auf
                  unseren kostenlosen Notion Planer – dein digitales Tagebuch für die Vorbereitung,
                  Durchführung und Nachbereitung deines Auslandsjahres.
                </p>
                <p>
                  Notion Planer enthalten ist ein exklusives Video mit den ehemaligen Austauschschülerinnen
                  Jackie und Sima, die berichten, wie sie ihren Eltern vom geplanten Auslandsjahr erzählt
                  haben, wie deren Reaktionen ausfielen und mit welchen Argumenten sie sie überzeugen konnten!
                </p>
              </>
            )
          }
      }

      break
    case 'sprachreisen':
      wording = {
      }
      break
    default:
      throw new Error(`No default upsell footer wording for businesArea '${businessArea}'`)
  }

  return wording
}
